// The version stack (NOW tier, Allyson-greenlit): every cut of the reel lands
// as a numbered row — v1, v2, v3 — with the newest marked Current, inline
// playback for Google Drive links (Drive's own /preview player: Google already
// transcoded the file; nobody downloads 2GB to review), and the approval
// stamp naming exactly which cut was blessed. The server flips an approved
// task back to needs-another-look when a newer cut lands (tasks.routes POST
// /:id/cuts) — this component only tells the story.
import { useState } from 'react';
import { Check, Play, Plus } from 'lucide-react';
import type { TaskCut } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useToastStore } from '@/core/ui/toastStore';
import { useTaskDetailStore } from '../store/taskDetailStore';

/** Drive file id from any of the share-link shapes; null for non-Drive URLs. */
function driveFileId(url: string): string | null {
  const m = /drive\.google\.com\/(?:file\/d\/|open\?id=)([A-Za-z0-9_-]{10,})/.exec(url);
  return m ? m[1] : null;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function CutsSection({ taskId, cuts }: { taskId: number; cuts: TaskCut[] }) {
  const toast = useToastStore((s) => s.show);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);

  const add = async () => {
    const u = url.trim();
    if (!u || busy) return;
    if (!/^https?:\/\/\S+$/i.test(u)) { toast('That doesn’t look like a link — paste the cut’s share link.', 'error'); return; }
    setBusy(true);
    try {
      await api.post(`/tasks/${taskId}/cuts`, { url: u });
      setUrl('');
      await useTaskDetailStore.getState().refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not add the cut.', 'error');
    }
    setBusy(false);
  };

  const newest = cuts.length ? Math.max(...cuts.map((c) => c.seq)) : 0;
  // Newest first — the current cut is what a reviewer opens the card FOR.
  const ordered = [...cuts].sort((a, b) => b.seq - a.seq);

  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">
          Cuts{cuts.length ? ` · ${cuts.length}` : ''}
        </span>
      </div>

      {ordered.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-line">
          {ordered.map((c) => {
            const drive = driveFileId(c.url);
            const isCurrent = c.seq === newest;
            return (
              <div key={c.id} className="border-b border-line last:border-0">
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${isCurrent ? 'bg-brand/15 text-brand' : 'bg-hover text-muted'}`}>v{c.seq}</span>
                  {isCurrent && <span className="text-[10px] font-medium uppercase tracking-wide text-brand">Current</span>}
                  {c.approvedByName && (
                    <span className="inline-flex items-center gap-1 rounded bg-success-soft px-1.5 py-0.5 text-[11px] font-medium text-success">
                      <Icon i={Check} size={14} /> Approved by {c.approvedByName}
                    </span>
                  )}
                  {c.label && <span className="min-w-0 truncate text-[13px] text-ink">{c.label}</span>}
                  <span className="ml-auto text-[11px] text-faint">{c.addedByName} · {fmtWhen(c.addedAt)}</span>
                  {drive ? (
                    <button
                      onClick={() => setPlaying(playing === c.id ? null : c.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] text-muted transition hover:bg-hover hover:text-ink"
                    >
                      <Icon i={Play} size={14} /> {playing === c.id ? 'Hide' : 'Play'}
                    </button>
                  ) : (
                    <a href={c.url} target="_blank" rel="noopener" className="rounded-md border border-line px-2 py-1 text-[11px] text-muted transition hover:bg-hover hover:text-ink">Open</a>
                  )}
                </div>
                {playing === c.id && drive && (
                  <div className="border-t border-line bg-black/20">
                    {/* Drive's own player; 16:9 box. allow-list keeps it to playback. */}
                    <iframe
                      src={`https://drive.google.com/file/d/${drive}/preview`}
                      title={`Cut v${c.seq}`}
                      className="aspect-video w-full"
                      allow="autoplay; fullscreen"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2">
        <span className="text-faint"><Icon i={Plus} size={14} /></span>
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }}
          placeholder={busy ? 'Adding…' : cuts.length ? `Paste the link to v${newest + 1}…  (Enter)` : 'Paste the Drive link to the first cut…  (Enter)'}
          disabled={busy}
          className="w-full bg-transparent py-0.5 text-[13px] text-ink outline-none placeholder:text-faint"
        />
      </div>
    </div>
  );
}
